import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Camera, CheckCircle, Plus, Trash, UserCirclePlus, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, fileUrl, formatApiErrorDetail } from "../lib/api";
import GoogleButton from "../components/GoogleButton";

const CLASS_OPTIONS = [
    "Tim Hijau (Pemula)",
    "Tim Kuning",
    "Tim Jingga",
    "Tim Biru",
    "Tim Pink",
    "Tim Merah",
    "Speed",
    "Slalom",
];

const emptyAthlete = () => ({
    full_name: "",
    photo_url: "",
    nickname: "",
    birth_place: "",
    birth_date: "",
    gender: "",
    joined_year: new Date().getFullYear(),
    class_name: "",
    religion: "",
    address: "",
    referral_source: "",
    promo_code: "",
});

export default function DaftarUlang({ mode = "renewal" }) {
    const isNewStudent = mode === "new";
    const { user, registerParent } = useAuth();
    const navigate = useNavigate();
    const [account, setAccount] = useState({
        username: "",
        password: "",
        email: "",
        whatsapp: "",
        father_name: "",
        mother_name: "",
    });
    const [athletes, setAthletes] = useState([emptyAthlete()]);
    const [uploading, setUploading] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [approvalResult, setApprovalResult] = useState(null);

    if (user) return <Navigate to={["admin", "finance", "coach"].includes(user.role) ? "/admin" : "/portal"} replace />;

    const setAthlete = (index, field, value) => {
        setAthletes((prev) => prev.map((athlete, i) => (i === index ? { ...athlete, [field]: value } : athlete)));
    };

    const addAthlete = () => setAthletes((prev) => [...prev, emptyAthlete()]);
    const removeAthlete = (index) => setAthletes((prev) => prev.filter((_, i) => i !== index));

    const uploadPhoto = async (index, file) => {
        if (!file) return;
        setUploading(index);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/public/upload-athlete-photo", fd);
            setAthlete(index, "photo_url", data.url);
            toast.success("Foto atlet terunggah");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setUploading(null);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const payload = {
                ...account,
                form_type: isNewStudent ? "siswa_baru" : "daftar_ulang",
                athletes: athletes.map((athlete) => ({
                    ...athlete,
                    joined_year: Number(athlete.joined_year),
                })),
            };
            const result = await registerParent(payload);
            if (result.requires_approval) {
                setApprovalResult({
                    ...result,
                    username: account.username,
                    password: account.password,
                });
                toast.success("Pendaftaran siswa baru terkirim. Menunggu ACC admin.");
                window.scrollTo({ top: 0, behavior: "smooth" });
                return;
            }
            if (result.duplicate_warnings > 0) {
                toast.success(isNewStudent ? "Pendaftaran siswa baru berhasil. Sistem menandai kemungkinan data ganda untuk dicek admin." : "Daftar ulang berhasil. Sistem menandai kemungkinan data ganda untuk dicek admin.");
            } else {
                toast.success(isNewStudent ? "Pendaftaran siswa baru berhasil. Akun langsung aktif — silakan selesaikan pembayaran pendaftaran di bagian Iuran & Pembayaran." : "Daftar ulang berhasil. Akun langsung aktif — silakan cek bagian Iuran & Pembayaran.");
            }
            navigate("/portal");
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FBF9F5] text-neutral-900" data-testid="reregistration-page">
            <div className="max-w-6xl mx-auto px-6 py-8 md:py-12">
                <div className="flex items-center justify-between gap-4 mb-12">
                    <Link to="/" data-testid="reregistration-back-home" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-racing-muted hover:text-racing-red transition-colors">
                        <ArrowLeft size={16} /> Kembali ke Beranda
                    </Link>
                    <Link to="/login" data-testid="reregistration-login-link" className="text-xs uppercase tracking-[0.2em] text-racing-red hover:text-racing-redhover transition-colors">
                        Sudah punya akun?
                    </Link>
                </div>

                <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }} className="mb-12">
                    <p className="text-[11px] uppercase tracking-[0.35em] text-racing-gold mb-4">{isNewStudent ? "Pendaftaran Anggota Baru" : "Menu Daftar Ulang"}</p>
                    <h1 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
                        {isNewStudent ? <>Daftar <span className="text-racing-red">Siswa Baru</span></> : <>Daftar <span className="text-racing-red">Ulang</span></>}
                    </h1>
                    <p className="mt-5 max-w-3xl text-base md:text-lg text-racing-muted leading-relaxed">
                        {isNewStudent
                            ? "Formulir pendaftaran siswa baru langsung di website Mandalika Inlineskate. Satu akun orang tua dapat digunakan untuk lebih dari satu atlet."
                            : "Semua orang tua membuat akun baru. Satu akun dapat digunakan untuk lebih dari satu atlet. Setelah formulir dikirim, akun langsung aktif dan data anak masuk ke Portal Orang Tua."}
                    </p>
                    <div className="mt-7 max-w-md" data-testid="reregistration-google-section">
                        <GoogleButton context="register" label="Daftar dengan Google" testid="reregistration-google-button" />
                        <p className="mt-3 text-[11px] text-racing-muted leading-relaxed">
                            Akun langsung aktif dan Anda bisa langsung menambahkan data anak dari portal. Jika email Google Anda sudah terdaftar, Anda akan otomatis masuk ke akun tersebut.
                        </p>
                        <div className="mt-5 flex items-center gap-4">
                            <span className="flex-1 h-px bg-racing-line" />
                            <span className="text-[10px] uppercase tracking-[0.25em] text-racing-muted">atau isi formulir</span>
                            <span className="flex-1 h-px bg-racing-line" />
                        </div>
                    </div>
                </motion.div>

                {approvalResult && (
                    <motion.section
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-10 bg-white border-2 border-racing-gold p-6 md:p-8"
                        data-testid="new-student-approval-confirmation"
                    >
                        <div className="flex items-start gap-4">
                            <span className="w-12 h-12 bg-racing-red text-white flex items-center justify-center shrink-0"><CheckCircle size={26} weight="fill" /></span>
                            <div className="flex-1">
                                <h2 className="font-display uppercase text-3xl md:text-4xl text-neutral-950">Menunggu ACC Admin</h2>
                                <p className="mt-4 text-base md:text-lg whitespace-pre-line text-neutral-900" data-testid="approval-confirmation-text">{approvalResult.message}</p>
                                <div className="mt-6 grid sm:grid-cols-2 gap-px bg-racing-line border border-racing-line max-w-xl">
                                    <div className="bg-racing-surface p-4">
                                        <p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-1">Username</p>
                                        <p className="font-medium break-all" data-testid="approval-username">{approvalResult.username}</p>
                                    </div>
                                    <div className="bg-racing-surface p-4">
                                        <p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-1">Password</p>
                                        <p className="font-medium break-all" data-testid="approval-password">{approvalResult.password}</p>
                                    </div>
                                </div>
                                <a
                                    href={approvalResult.confirmation_wa_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="approval-wa-link"
                                    className="mt-6 inline-flex items-center gap-2 bg-[#128C7E] hover:bg-[#0d6f64] text-white text-xs uppercase tracking-[0.2em] px-5 py-3 transition-colors"
                                >
                                    <WhatsappLogo size={18} weight="fill" /> Konfirmasi ke WA 085230009454
                                </a>
                            </div>
                        </div>
                    </motion.section>
                )}

                <form onSubmit={submit} className="space-y-10" data-testid="reregistration-form">
                    <section className="bg-white border border-racing-line p-6 md:p-8">
                        <div className="flex items-center gap-3 mb-8">
                            <span className="w-10 h-10 bg-racing-red flex items-center justify-center text-white"><UserCirclePlus size={22} /></span>
                            <div>
                                <h2 className="font-display uppercase text-2xl">Akun Orang Tua</h2>
                                <p className="text-xs uppercase tracking-[0.2em] text-racing-muted mt-1">Username dibuat sendiri</p>
                            </div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Username</label>
                                <input required minLength={3} value={account.username} onChange={(e) => setAccount({ ...account, username: e.target.value })} data-testid="rereg-username-input" className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red" placeholder="contoh: budi.santoso" />
                                <p className="mt-1.5 text-xs text-racing-red" data-testid="rereg-username-hint">Tanpa spasi. Gunakan titik atau strip, contoh: budi.santoso atau budi-santoso</p>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Password</label>
                                <input required minLength={6} type="password" value={account.password} onChange={(e) => setAccount({ ...account, password: e.target.value })} data-testid="rereg-password-input" className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red" placeholder="Minimal 6 karakter" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Email</label>
                                <input required type="email" value={account.email} onChange={(e) => setAccount({ ...account, email: e.target.value })} data-testid="rereg-email-input" className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red" placeholder="nama@email.com" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">No. WhatsApp</label>
                                <input required value={account.whatsapp} onChange={(e) => setAccount({ ...account, whatsapp: e.target.value })} data-testid="rereg-whatsapp-input" className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red" placeholder="08xxxxxxxxxx" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Nama Ayah</label>
                                <input value={account.father_name} onChange={(e) => setAccount({ ...account, father_name: e.target.value })} data-testid="rereg-father-input" className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red" placeholder="Nama Ayah/Wali" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Nama Ibu</label>
                                <input value={account.mother_name} onChange={(e) => setAccount({ ...account, mother_name: e.target.value })} data-testid="rereg-mother-input" className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red" placeholder="Nama Ibu/Wali" />
                            </div>
                        </div>
                        <p className="mt-4 text-xs text-racing-muted">Isi Nama Ayah atau Nama Ibu minimal salah satu.</p>
                    </section>

                    <section className="space-y-6">
                        <div>
                            <h2 className="font-display uppercase text-3xl">Data Atlet</h2>
                            <p className="text-sm text-racing-muted mt-2">Isi data anak/atlet Anda. Jika mendaftarkan lebih dari satu anak, tombol Tambah Atlet ada di bagian bawah formulir.</p>
                        </div>

                        {athletes.map((athlete, index) => (
                            <motion.section key={index} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="bg-white border border-racing-line p-6 md:p-8" data-testid={`rereg-athlete-card-${index}`}>
                                <div className="flex items-center justify-between gap-4 mb-7">
                                    <p className="text-[11px] uppercase tracking-[0.25em] text-racing-gold">Atlet {index + 1}</p>
                                    {athletes.length > 1 && (
                                        <button type="button" onClick={() => removeAthlete(index)} data-testid={`rereg-remove-athlete-${index}`} className="p-2 border border-racing-line text-racing-red hover:bg-racing-red hover:text-white transition-colors" aria-label="Hapus atlet">
                                            <Trash size={16} />
                                        </button>
                                    )}
                                </div>

                                <div className="grid md:grid-cols-2 gap-5">
                                    <div>
                                        <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Nama Lengkap Atlet</label>
                                        <input required value={athlete.full_name} onChange={(e) => setAthlete(index, "full_name", e.target.value)} data-testid={`rereg-athlete-name-${index}`} className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red" />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Nama Panggilan</label>
                                        <input value={athlete.nickname} onChange={(e) => setAthlete(index, "nickname", e.target.value)} data-testid={`rereg-athlete-nickname-${index}`} className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red" />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Tempat Lahir</label>
                                        <input required value={athlete.birth_place} onChange={(e) => setAthlete(index, "birth_place", e.target.value)} data-testid={`rereg-athlete-birth-place-${index}`} className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red" placeholder="Contoh: Mataram" />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Tanggal Lahir</label>
                                        <input required type="date" value={athlete.birth_date} onChange={(e) => setAthlete(index, "birth_date", e.target.value)} data-testid={`rereg-athlete-birth-date-${index}`} className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red" />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Jenis Kelamin</label>
                                        <select required value={athlete.gender} onChange={(e) => setAthlete(index, "gender", e.target.value)} data-testid={`rereg-athlete-gender-${index}`} className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red">
                                            <option value="" disabled>Pilih jenis kelamin</option>
                                            <option value="Laki-laki">Laki-laki</option>
                                            <option value="Perempuan">Perempuan</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Masuk Mandalis Sejak</label>
                                        <input required type="number" min="2000" max={new Date().getFullYear() + 1} value={athlete.joined_year} onChange={(e) => setAthlete(index, "joined_year", e.target.value)} data-testid={`rereg-athlete-joined-year-${index}`} className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Kelas</label>
                                        <select required value={athlete.class_name} onChange={(e) => setAthlete(index, "class_name", e.target.value)} data-testid={`rereg-athlete-class-${index}`} className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red">
                                            <option value="" disabled>Pilih kelas</option>
                                            {CLASS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Agama</label>
                                        <input value={athlete.religion} onChange={(e) => setAthlete(index, "religion", e.target.value)} data-testid={`rereg-athlete-religion-${index}`} className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red" placeholder="Contoh: Islam" />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Alamat Rumah</label>
                                        <input value={athlete.address} onChange={(e) => setAthlete(index, "address", e.target.value)} data-testid={`rereg-athlete-address-${index}`} className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red" placeholder="Alamat domisili" />
                                    </div>
                                    {isNewStudent && (
                                        <>
                                            <div className="md:col-span-2">
                                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Darimana Tahu Mandalis?</label>
                                                <input value={athlete.referral_source} onChange={(e) => setAthlete(index, "referral_source", e.target.value)} data-testid={`new-athlete-referral-${index}`} className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red" placeholder="Contoh: Instagram, atau dari orang tua Ananda..." />
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Kode Promo (untuk Free Kaos)</label>
                                                <input value={athlete.promo_code} onChange={(e) => setAthlete(index, "promo_code", e.target.value)} data-testid={`new-athlete-promo-${index}`} className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red" placeholder="Masukkan kode promo jika ada" />
                                            </div>
                                        </>
                                    )}
                                    <div className="md:col-span-2">
                                        <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Foto Atlet (opsional)</label>
                                        <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
                                            {athlete.photo_url ? (
                                                <img src={fileUrl(athlete.photo_url)} alt={`Foto ${athlete.full_name || "atlet"}`} className="w-24 h-24 object-cover border border-racing-line" data-testid={`rereg-athlete-photo-preview-${index}`} />
                                            ) : (
                                                <div className="w-24 h-24 bg-racing-surface border border-dashed border-racing-line flex items-center justify-center text-racing-muted"><Camera size={24} /></div>
                                            )}
                                            <label className="flex-1 border border-dashed border-racing-line bg-racing-surface px-4 py-4 cursor-pointer text-sm text-racing-muted">
                                                {uploading === index ? "Mengunggah foto..." : athlete.photo_url ? "Ganti foto" : "Pilih foto atlet"}
                                                <input type="file" accept="image/*" className="hidden" data-testid={`rereg-athlete-photo-${index}`} onChange={(e) => uploadPhoto(index, e.target.files?.[0])} />
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </motion.section>
                        ))}

                        <div className="border-2 border-dashed border-racing-gold/60 bg-racing-gold/5 p-6 text-center" data-testid="rereg-add-athlete-section">
                            <p className="text-sm text-neutral-900 font-medium">Mendaftarkan anak lebih dari satu dalam akun ini?</p>
                            <p className="text-xs text-racing-muted mt-1 mb-4">Pastikan data atlet di atas sudah lengkap, lalu klik tombol di bawah ini untuk menambah atlet berikutnya.</p>
                            <button type="button" onClick={addAthlete} data-testid="rereg-add-athlete" className="inline-flex items-center gap-2 border border-racing-red text-racing-red hover:bg-racing-red hover:text-white text-xs uppercase tracking-[0.2em] px-6 py-3.5 transition-colors">
                                <Plus size={16} /> Tambah Atlet
                            </button>
                        </div>
                    </section>

                    {error && <p className="text-sm text-racing-red" data-testid="reregistration-error">{error}</p>}

                    <div className="border border-racing-gold/50 bg-racing-gold/10 p-5" data-testid="registration-consent-text">
                        <p className="text-xs md:text-sm text-neutral-800 leading-relaxed">
                            Dengan mendaftarkan anak saya di klub Mandalis saya setuju untuk menaati tata tertib yang ada di Mandalis, mempersiapkan alat keselamatan untuk anak saya <span className="font-semibold">(Wajib pakai helm)</span>, menyediakan asuransi minimal <span className="font-semibold">BPJS Ketenagakerjaan Bakat Minat Atlet</span> sebagai asuransi jika terjadi resiko dan tidak keberatan dengan dokumentasi dan publikasi selama latihan.
                        </p>
                    </div>

                    <div className="sticky bottom-0 bg-[#FBF9F5]/95 backdrop-blur border-t border-racing-line py-5">
                        <button type="submit" disabled={loading || uploading !== null} data-testid="reregistration-submit" className="w-full bg-racing-red hover:bg-racing-redhover disabled:opacity-50 text-white text-xs uppercase tracking-[0.25em] py-4 transition-colors">
                            {loading ? "Menyimpan..." : isNewStudent ? "Kirim Pendaftaran Siswa Baru" : "Kirim Daftar Ulang & Aktifkan Akun"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
